import React, { useState } from "react";
import { Alert, Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import {
  Envelope,
  ExclamationTriangle,
  ShieldCheck,
  Trash,
} from "react-bootstrap-icons";
import axios from "axios";
import API_BASE_URL from "../config/api";
import PublicLayout from "../components/PublicLayout";

const initialForm = {
  email: "",
  reason: "",
};

const DeleteAccountRequest = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSuccess("");
    setError("");

    try {
      setSubmitting(true);
      await axios.post(`${API_BASE_URL}/api/auth/delete-account-request`, form, {
        headers: { "Content-Type": "application/json" },
      });
      setSuccess(
        "Your account deletion request has been submitted. You will not be able to log in while the request is pending."
      );
      setForm(initialForm);
    } catch (err) {
      console.error("Delete account request error:", err);
      setError(
        err?.response?.data?.message ||
          "We could not submit your request right now. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout
      eyebrow="Account Privacy"
      title="Delete Account Request"
      description="Submit a request to delete your Owner Builder account and related platform data."
    >
      <Row className="g-4 align-items-stretch">
        <Col lg={5}>
          <div className="contact-info-panel">
            <div className="contact-icon">
              <ShieldCheck />
            </div>
            <h2>Before you submit</h2>
            <p>
              Once your request is submitted, your account login will be paused
              until an admin reviews the request.
            </p>
            <div className="contact-info-list">
              <div>
                <strong>Pending request</strong>
                <span>You will not be able to log in while the request is pending.</span>
              </div>
              <div>
                <strong>If approved</strong>
                <span>Your account and related platform traces will be removed.</span>
              </div>
              <div>
                <strong>If rejected</strong>
                <span>Your account access will be restored normally.</span>
              </div>
            </div>
          </div>
        </Col>

        <Col lg={7}>
          <Card className="public-form-card">
            <Card.Body className="p-4 p-md-5">
              <Alert variant="warning" className="d-flex gap-2">
                <ExclamationTriangle className="mt-1 flex-shrink-0" />
                <span>
                  Submitting this request will prevent login until the admin
                  review is complete.
                </span>
              </Alert>

              {success && <Alert variant="success">{success}</Alert>}
              {error && <Alert variant="danger">{error}</Alert>}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <div className="public-input-wrap">
                    <Envelope />
                    <Form.Control
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      required
                      disabled={submitting}
                    />
                  </div>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Reason</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={7}
                    name="reason"
                    value={form.reason}
                    onChange={handleChange}
                    placeholder="Tell us why you want to delete your account"
                    required
                    disabled={submitting}
                  />
                </Form.Group>

                <div className="d-flex justify-content-end">
                  <Button
                    className="public-submit-btn"
                    type="submit"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <Spinner size="sm" animation="border" className="me-2" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Trash className="me-2" />
                        Submit Request
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </PublicLayout>
  );
};

export default DeleteAccountRequest;
